import { z } from 'zod/v4';

/**
 * Colour value as the theme system stores it — either a plain CSS colour
 * string or a gradient stop array. Mirrors `ThemeButtonDataLegacy['color']`
 * (`string | string[]`) from `packages/site-theme/src/types/theme-button.type.ts`.
 */
export const themeColorSchemaLegacy = z.union([z.string(), z.array(z.string())]);

/**
 * CSS dimension as the theme system stores it — number (px) or string with
 * an explicit unit. Mirrors `ThemeButtonDataLegacy['borderRadius' | 'borderWidth']`.
 */
export const cssDimensionSchemaLegacy = z.union([z.string(), z.number()]);

/**
 * Base (rest-state) styling bag for themed buttons — matches `ThemeButtonDataLegacy`
 * from the host app's theme types and the `buttonBase` control produced by
 * `getButtonItemFormGroup` in the button widget's form factory.
 */
export const themeButtonDataSchemaLegacy = z
    .object({
        borderRadius: cssDimensionSchemaLegacy,
        borderStyle: z.string(),
        borderWidth: cssDimensionSchemaLegacy,
        borderColor: themeColorSchemaLegacy,
        color: themeColorSchemaLegacy,
        background: themeColorSchemaLegacy,
    })
    .describe('Base (rest-state) styling — colors, border, etc.');

export type ThemeButtonDataLegacy = z.infer<typeof themeButtonDataSchemaLegacy>;

/**
 * Deep-partial variant of `themeButtonDataSchemaLegacy` — every sub-field optional, for callers
 * that accept a PATCH onto an existing/default `ThemeButtonDataLegacy` rather than a full
 * replacement (e.g. the AI connector's `buttonBase` args, where a model changing just the
 * background shouldn't have to resupply border/radius/color it never touched). `.partial()`
 * called directly on an object schema doesn't carry over the object's own `.describe()`, so it's
 * re-declared here rather than left to the caller.
 */
export const themeButtonDataPartialSchemaLegacy = themeButtonDataSchemaLegacy
    .partial()
    .describe(
        'Base (non-hover) styling — colors, border, etc. Send just the sub-fields you want to change; ' +
            "other sub-fields are left untouched. Only rendered when styleType is 'custom'."
    );

/**
 * Hover behaviour kind. Mirrors `EButtonHoverType` from `@common/enums`.
 */
export const buttonHoverTypeSchemaLegacy = z.enum(['opacity', 'color', 'shadow']);

/**
 * Hover-state styling bag for themed buttons — matches `ThemeButtonHoverLegacy`
 * and the `buttonHover` control of the button item form.
 */
export const themeButtonHoverSchemaLegacy = z
    .object({
        hoverType: buttonHoverTypeSchemaLegacy,
        opacity: z.number(),
        shadow: z.string(),
        color: themeColorSchemaLegacy,
        background: themeColorSchemaLegacy,
        borderColor: themeColorSchemaLegacy,
    })
    .describe('Hover-state styling — colors, border, opacity/shadow, etc.');

export type ThemeButtonHoverLegacy = z.infer<typeof themeButtonHoverSchemaLegacy>;

/**
 * Default rest-state bag. Mirrors `DEFAULT_BUTTON_BASE` from
 * `apps/websites/src/app/widgets/common/base/base-button/base-button.const.ts`.
 */
export const createThemeButtonDataLegacy = (overrides: Partial<ThemeButtonDataLegacy> = {}): ThemeButtonDataLegacy => ({
    color: ['rgb(255,255,255)'],
    background: ['rgb(0,0,0)'],
    borderColor: ['rgb(0,0,0)'],
    borderWidth: 2,
    borderRadius: 8,
    borderStyle: 'solid',
    ...overrides,
});

/**
 * Default hover-state bag. Mirrors `DEFAULT_BUTTON_HOVER_STATES` from the
 * same host-app constants module.
 */
export const createThemeButtonHoverLegacy = (
    overrides: Partial<ThemeButtonHoverLegacy> = {}
): ThemeButtonHoverLegacy => ({
    hoverType: 'opacity',
    opacity: 80,
    shadow: 'none',
    color: ['rgb(255,255,255)'],
    background: ['rgb(22,117,242)'],
    borderColor: ['rgba(0,0,0,0)'],
    ...overrides,
});
